import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Card, Button, Space, Tag, Typography, Form, Input, Select,
  Row, Col, Statistic, Modal, message, Cascader
} from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, SelectOutlined } from '@ant-design/icons';
import SearchSelect from '../../../components/SearchSelect/SearchSelect';

const { Title, Text } = Typography;

// TODO: 联调时替换为接口数据
const MOCK_ECOSYSTEM = [
  { key: '1', productName: '智能云台摄像机方案', productId: 'P_ECO_001', category: '摄像机', vendorName: '深圳芯视界科技有限公司', vendorId: 'ENT_V01', description: '基于海思Hi3516DV300的全功能云台摄像机方案，支持1080P、云存、AI人形检测、语音对讲', stdAbilityCount: 12, customAbilityCount: 3, fwVersionCount: 4, openRange: 'public', listTime: '2026-04-20 10:00:00', hardwarePrice: 89.00 },
  { key: '2', productName: '4G低功耗电池摄像机方案', productId: 'P_ECO_002', category: '摄像机', vendorName: '杭州云联物联网有限公司', vendorId: 'ENT_V02', description: '超低功耗4G电池摄像机方案，支持PIR检测唤醒、太阳能充电、30天续航', stdAbilityCount: 8, customAbilityCount: 5, fwVersionCount: 2, openRange: 'public', listTime: '2026-04-18 14:30:00', isSelf: true, hardwarePrice: 129.00 },
  { key: '3', productName: '智能门铃方案', productId: 'P_ECO_003', category: '摄像机', vendorName: '深圳芯视界科技有限公司', vendorId: 'ENT_V01', description: '可视门铃方案，支持人脸识别、双向语音、变声对讲、PIR检测', stdAbilityCount: 10, customAbilityCount: 2, fwVersionCount: 3, openRange: 'directed', listTime: '2026-04-15 09:00:00', hardwarePrice: 65.00 },
  { key: '4', productName: 'NVR网络录像机方案', productId: 'P_ECO_004', category: '存储类', vendorName: '东莞康林物联网有限公司', vendorId: 'ENT_V03', description: '4/8/16路NVR方案，支持H.265解码、多路回放、云端备份', stdAbilityCount: 6, customAbilityCount: 1, fwVersionCount: 2, openRange: 'public', listTime: '2026-04-10 16:00:00', hardwarePrice: 199.00 },
];

const CATEGORY_OPTIONS = [
  { label: '摄像机', value: '1', children: [
    { label: '室内摄像机', value: '1-1' },
    { label: '户外摄像机', value: '1-2' },
    { label: '4G智能摄像机', value: '1-3' },
    { label: '融合摄像机', value: '1-4' },
  ]},
  { label: '网关类', value: '2' },
  { label: '存储类', value: '3' },
];

// 选用时的目标企业（派生产品归属方）— TODO: 联调时替换为接口数据
const TARGET_ENT_OPTIONS = [
  { value: 'self', label: '本企业（方案商自身，客户未入驻平台时由方案商代管）' },
  { value: 'ENT_M01', label: '东莞康林物联网有限公司（ENT_M01）' },
  { value: 'ENT_M02', label: '成都云视科技有限公司（ENT_M02）' },
  { value: 'ENT_M03', label: '广州云联物联网有限公司（ENT_M03）' },
];

// TODO: 联调时替换为真实接口
const fetchProductOptions = (keyword) =>
  new Promise((resolve) => setTimeout(() => resolve(
    MOCK_ECOSYSTEM.filter((p) => p.productName.includes(keyword)).map((p) => ({ value: p.productId, label: p.productName }))
  ), 300));

const fetchVendorOptions = (keyword) =>
  new Promise((resolve) => setTimeout(() => resolve(
    [...new Map(MOCK_ECOSYSTEM.filter((p) => p.vendorName.includes(keyword)).map((p) => [p.vendorId, { value: p.vendorId, label: p.vendorName }])).values()]
  ), 300));

const EcosystemList = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [selectModalVisible, setSelectModalVisible] = useState(false);
  const [selectRecord, setSelectRecord] = useState(null);
  const [derivedName, setDerivedName] = useState('');
  const [targetEnt, setTargetEnt] = useState('self');

  const handleSelect = (record) => {
    setSelectRecord(record);
    setDerivedName(`${record.productName}-我的企业`);
    setTargetEnt('self');
    setSelectModalVisible(true);
  };

  const handleConfirmSelect = () => {
    if (!derivedName.trim()) { message.warning('请输入派生产品名称'); return; }
    const targetLabel = TARGET_ENT_OPTIONS.find((o) => o.value === targetEnt)?.label || '';
    const owner = targetEnt === 'self' ? '本企业' : targetLabel.split('（')[0];
    message.success(`选用成功，派生产品「${derivedName}」归属：${owner}`);
    setSelectModalVisible(false);
    Modal.confirm({
      title: '选用成功',
      content: `派生产品「${derivedName}」已创建（归属：${owner}），是否前往产品开发列表查看？`,
      okText: '前往查看', cancelText: '留在当前页',
      onOk: () => navigate('/product'),
    });
  };

  const columns = [
    {
      title: '方案产品', key: 'product', width: 260,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.productName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text>
        </div>
      ),
    },
    { title: '产品类目', dataIndex: 'category', width: 200 },
    {
      title: '方案商', key: 'vendor', width: 180,
      render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.vendorName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.vendorId}</Text></div>),
    },
    { title: '方案描述', dataIndex: 'description', width: 280, ellipsis: true },
    { title: '固件版本', dataIndex: 'fwVersionCount', width: 90, align: 'center', render: (v) => `${v} 个` },
    { title: '硬件定价', dataIndex: 'hardwarePrice', width: 100, align: 'right', render: (v) => v != null ? `¥${v.toFixed(2)}` : '-' },
    {
      title: '开放范围', dataIndex: 'openRange', width: 90, align: 'center',
      render: (v) => <Tag color={v === 'public' ? 'blue' : 'orange'}>{v === 'public' ? '公开' : '定向'}</Tag>,
    },
    { title: '上架时间', dataIndex: 'listTime', width: 170 },
    {
      title: '操作', key: 'action', width: 160, fixed: 'right', align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/product/ecosystem/detail?id=${r.productId}`, { state: { isSelf: !!r.isSelf } })}>查看</Button>
          <Button type="link" size="small" icon={<SelectOutlined />} onClick={() => handleSelect(r)}>选用</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}><Title level={4}>生态方案库</Title></div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}><Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}><Statistic title="方案总数" value={MOCK_ECOSYSTEM.length} valueStyle={{ color: '#1890ff', fontWeight: 'bold' }} /></Card></Col>
        <Col span={6}><Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}><Statistic title="已选用数" value={1} valueStyle={{ color: '#52c41a', fontWeight: 'bold' }} /></Card></Col>
      </Row>

      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form form={form} layout="inline">
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="prodType" initialValue="name" noStyle><Select style={{ width: 100 }}><Select.Option value="name">产品名称</Select.Option><Select.Option value="id">产品ID</Select.Option></Select></Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.prodType !== cur.prodType}>
                {({ getFieldValue }) => getFieldValue('prodType') === 'id'
                  ? <Form.Item name="prodValue" noStyle><Input placeholder="输入产品ID" allowClear style={{ width: 200 }} /></Form.Item>
                  : <Form.Item name="prodValue" noStyle><SearchSelect fetchOptions={fetchProductOptions} placeholder="搜索方案产品" style={{ width: 200 }} /></Form.Item>}
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="category" style={{ marginRight: 8 }}><Cascader options={CATEGORY_OPTIONS} placeholder="产品类目" allowClear style={{ width: 200 }} /></Form.Item>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="vendorType" initialValue="name" noStyle><Select style={{ width: 100 }}><Select.Option value="name">方案商</Select.Option><Select.Option value="id">企业ID</Select.Option></Select></Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.vendorType !== cur.vendorType}>
                {({ getFieldValue }) => getFieldValue('vendorType') === 'id'
                  ? <Form.Item name="vendorValue" noStyle><Input placeholder="输入企业ID" allowClear style={{ width: 200 }} /></Form.Item>
                  : <Form.Item name="vendorValue" noStyle><SearchSelect fetchOptions={fetchVendorOptions} placeholder="搜索方案商" style={{ width: 200 }} /></Form.Item>}
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item><Space><Button type="primary" icon={<SearchOutlined />}>查询</Button><Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button></Space></Form.Item>
        </Form>
      </Card>

      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Table columns={columns} dataSource={MOCK_ECOSYSTEM} rowKey="key" scroll={{ x: 1500 }} style={{ whiteSpace: 'nowrap' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} />
      </Card>

      <Modal title="选用方案产品" open={selectModalVisible} onCancel={() => setSelectModalVisible(false)} onOk={handleConfirmSelect} okText="确认选用" width={480} destroyOnClose>
        {selectRecord && (
          <div style={{ marginTop: 16 }}>
            <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}><Text type="secondary">方案产品：</Text><Text strong>{selectRecord.productName}</Text></div>
              <div><Text type="secondary">方案商：</Text><Text>{selectRecord.vendorName}</Text></div>
            </div>
            <div style={{ marginBottom: 8 }}><Text strong>派生产品名称 <Text type="danger">*</Text></Text></div>
            <Input value={derivedName} onChange={(e) => setDerivedName(e.target.value)} placeholder="请输入派生产品名称" maxLength={50} showCount />
            <div style={{ marginTop: 16, marginBottom: 8 }}><Text strong>目标企业（派生产品归属）</Text></div>
            <Select value={targetEnt} onChange={setTargetEnt} style={{ width: '100%' }} options={TARGET_ENT_OPTIONS} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              可选择客户企业；客户未入驻平台时选择「本企业」，由方案商代管产品与设备。
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EcosystemList;
